/**
 * @file Composes Firestore config documents into the app runtime config.
 */

import {
    DEFAULT_BRANDING,
    clampSurfaceTint,
    normalizeHexColor,
} from "../../shared/ui/brandingTokens.js";
import {
    getRequiredNotificationConfigEntries,
    getRequiredTeamNotificationConfigEntries,
} from "../notifications/notificationRegistryAdapter.js";

const NOTIFICATION_CONFIG_DEFAULTS = Object.freeze({
    adminNotificationControls: {},
    adminNotificationDefaults: {},
    teamNotificationSettings: {},
    notificationGroups: {},
    notificationEventLayout: {},
    calendarThresholdDefaults: {},
    notificationSchedule: {
        activeDays: [],
        holidays: [],
    },
    drugs: {
        notificationRoleTargets: {},
    },
});
const DEFAULT_BUSINESS_TIMEZONE = "Pacific/Auckland";

/**
 * Returns whether a value is a valid IANA timezone.
 *
 * @param {*} value Candidate timezone.
 * @returns {boolean} True when Intl can use the timezone.
 */
function isValidTimeZone(value) {
    const timeZone = String(value || "").trim();
    if (!timeZone) {return false;}
    try {
        new Intl.DateTimeFormat("en-AU", {timeZone});
        return true;
    } catch {
        return false;
    }
}

/**
 * Normalizes Business Calendar config onto the canonical config/main field.
 *
 * @param {object | null | undefined} businessCalendar Config/main business calendar.
 * @param {object | null | undefined} notificationSchedule Legacy schedule config.
 * @returns {object} Normalized Business Calendar config.
 */
function normalizeBusinessCalendarConfig(businessCalendar, notificationSchedule) {
    const source = businessCalendar || {};
    const legacySchedule = notificationSchedule || {};
    const candidateTimezone = isValidTimeZone(source.timezone) ?
        String(source.timezone).trim() :
        String(legacySchedule.timeZone || "").trim();

    return {
        ...source,
        timezone: isValidTimeZone(candidateTimezone) ?
            candidateTimezone :
            DEFAULT_BUSINESS_TIMEZONE,
    };
}

/**
 * Returns safe branding tokens for config/main persistence.
 *
 * @param {object | null | undefined} branding Branding config draft.
 * @returns {object} Normalized branding config.
 */
function normalizeBrandingConfig(branding) {
    const source = branding || {};
    return {
        ...source,
        primaryColor: normalizeHexColor(
            source.primaryColor,
            DEFAULT_BRANDING.primaryColor,
        ),
        primaryHoverColor: normalizeHexColor(
            source.primaryHoverColor,
            DEFAULT_BRANDING.primaryHoverColor,
        ),
        accentColor: normalizeHexColor(
            source.accentColor,
            DEFAULT_BRANDING.accentColor,
        ),
        secondaryAccentColor: normalizeHexColor(
            source.secondaryAccentColor,
            DEFAULT_BRANDING.secondaryAccentColor,
        ),
        surfaceTintOpacity: clampSurfaceTint(source.surfaceTintOpacity),
    };
}

function mergeRequiredNotificationEntries({
    controls,
    defaults,
    teamSettings,
}) {
    const nextControls = { ...(controls || {}) };
    const nextDefaults = { ...(defaults || {}) };
    const nextTeamSettings = { ...(teamSettings || {}) };

    const mergeConfiguredEntry = (existingEntry, requiredEntry) => {
        const merged = { ...requiredEntry };
        Object.keys(requiredEntry).forEach((field) => {
            if (existingEntry?.[field] !== undefined) {
                merged[field] = existingEntry[field];
            }
        });
        return merged;
    };

    Object.entries(getRequiredNotificationConfigEntries()).forEach(
        ([key, value]) => {
            if (value.control) {
                nextControls[key] = mergeConfiguredEntry(
                    nextControls[key],
                    value.control,
                );
            }
            if (value.default) {
                nextDefaults[key] = mergeConfiguredEntry(
                    nextDefaults[key],
                    value.default,
                );
            }
        },
    );

    Object.entries(getRequiredTeamNotificationConfigEntries()).forEach(
        ([key, value]) => {
            if (!nextTeamSettings[key]) {
                nextTeamSettings[key] = { ...value };
            }
        },
    );

    return {
        controls: nextControls,
        defaults: nextDefaults,
        teamSettings: nextTeamSettings,
    };
}

/**
 * Returns the config fallback used when Firestore config cannot be read.
 *
 * @returns {object} Safe empty config.
 */
export function getConfigFallback() {
    return {
        taskStatus: [],
        patientStatus: [],
        projectStatus: [],
        equipmentStatus: [],
        recallStatus: [],
        labStatus: [],
        labCategoryConfig: [],
        maintenanceStatus: [],
        priorities: [],
        labTypes: [],
        maintenanceTypes: [],
        recalls: {},
        labs: {},
        maintenance: {},
        providers: [],
        paymentMethods: [],
        patientCategoryConfig: [],
        projectCategoryConfig: [],
        assetCategoryConfig: [],
        taskCategoryConfig: [],
        maintenanceCategoryConfig: [],
        orderingLists: [],
        complianceCategories: [],
        complianceCategoryTeamIds: {},
        adminNotificationControls: {},
        adminNotificationDefaults: {},
        teamNotificationSettings: {},
        notificationGroups: {},
        notificationEventLayout: {},
        calendarThresholdDefaults: {},
        notificationSchedule: {
            activeDays: [],
            holidays: [],
        },
        calendar: {
            eventColor: "#DBEAFE",
            leaveColor: "#DCFCE7",
            nonBusinessDayColor: "#F3F4F6",
            holidayColor: "#FEF3C7",
        },
        leave: {
            types: [],
            approvalMode: "manager_only",
            detailTeamId: null,
            escalationTimeoutHours: 72,
        },
        businessCalendar: {
            timezone: "Pacific/Auckland",
            activeDays: [1, 2, 3, 4, 5],
            holidays: [],
        },
    };
}

/**
 * Returns a normalized notification config document shape.
 *
 * @param {object | null | undefined} notificationsConfig Firestore config/notifications data.
 * @returns {object} Notification-owned runtime config.
 */
export function normalizeNotificationConfig(notificationsConfig) {
    const source = notificationsConfig || {};
    const sourceDrugs = source.drugs || {};
    const required = mergeRequiredNotificationEntries({
        controls: source.adminNotificationControls,
        defaults: source.adminNotificationDefaults,
        teamSettings: source.teamNotificationSettings,
    });

    return {
        adminNotificationControls: required.controls,
        adminNotificationDefaults: required.defaults,
        teamNotificationSettings: required.teamSettings,
        notificationGroups: source.notificationGroups ?? {},
        notificationEventLayout: source.notificationEventLayout ?? {},
        calendarThresholdDefaults: source.calendarThresholdDefaults ?? {},
        notificationSchedule: source.notificationSchedule ??
            NOTIFICATION_CONFIG_DEFAULTS.notificationSchedule,
        drugs: {
            notificationRoleTargets: sourceDrugs.notificationRoleTargets ?? {},
        },
    };
}

/**
 * Composes config/main and config/notifications into one runtime config.
 *
 * @param {object | null | undefined} mainConfig Firestore config/main data.
 * @param {object | null | undefined} notificationsConfig Firestore config/notifications data.
 * @returns {object} Effective runtime config exposed through appData.config.
 */
export function assembleRuntimeConfig(mainConfig, notificationsConfig) {
    const baseConfig = mainConfig || getConfigFallback();
    const notificationConfig = normalizeNotificationConfig(notificationsConfig);
    const businessCalendar = normalizeBusinessCalendarConfig(
        baseConfig.businessCalendar,
        notificationConfig.notificationSchedule,
    );

    const runtimeConfig = {
        ...baseConfig,
        businessCalendar,
        adminNotificationControls: notificationConfig.adminNotificationControls,
        adminNotificationDefaults: notificationConfig.adminNotificationDefaults,
        teamNotificationSettings: notificationConfig.teamNotificationSettings,
        notificationGroups: notificationConfig.notificationGroups,
        notificationEventLayout: notificationConfig.notificationEventLayout,
        calendarThresholdDefaults:
            notificationConfig.calendarThresholdDefaults,
        notificationSchedule: notificationConfig.notificationSchedule,
        drugs: {
            ...(baseConfig.drugs || {}),
            ...notificationConfig.drugs,
        },
    };
    if (runtimeConfig.calendar) {
        delete runtimeConfig.calendar.defaultEventReminderLeadDays;
    }
    if (runtimeConfig.leave) {
        delete runtimeConfig.leave.defaultReminderLeadDays;
    }
    return runtimeConfig;
}

/**
 * Splits one merged Settings config into its source Firestore documents.
 *
 * @param {object} runtimeConfig Effective merged settings config.
 * @returns {{mainConfig: object, notificationConfig: object}} Persistable config documents.
 */
export function splitRuntimeConfigForPersistence(runtimeConfig) {
    const source = runtimeConfig || {};
    const {
        adminNotificationControls,
        adminNotificationDefaults,
        teamNotificationSettings,
        notificationGroups,
        notificationEventLayout,
        calendarThresholdDefaults,
        notificationSchedule,
        drugs,
        ...mainConfigBase
    } = source;
    const {
        notificationRoleTargets,
        ...mainDrugsConfig
    } = drugs || {};

    const notificationConfig = {
        adminNotificationControls: adminNotificationControls || {},
        adminNotificationDefaults: adminNotificationDefaults || {},
        teamNotificationSettings: teamNotificationSettings || {},
        notificationGroups: notificationGroups || {},
        notificationEventLayout: notificationEventLayout || {},
        calendarThresholdDefaults: calendarThresholdDefaults || {},
        notificationSchedule: notificationSchedule || {
            activeDays: [],
            holidays: [],
        },
        drugs: {
            notificationRoleTargets:
                notificationRoleTargets || {},
        },
    };

    const mainConfig = {
        ...mainConfigBase,
        branding: normalizeBrandingConfig(mainConfigBase.branding),
        ...(drugs ? { drugs: mainDrugsConfig } : {}),
    };
    if (mainConfig.calendar) {
        delete mainConfig.calendar.defaultEventReminderLeadDays;
    }
    if (mainConfig.leave) {
        delete mainConfig.leave.defaultReminderLeadDays;
    }

    return {
        mainConfig,
        notificationConfig,
    };
}
